import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { colorCodesForAvatar } from "../../../../constants/index";
import { chracterPosition } from "../../../../utils/index";
import { buildTeamDetailView } from "./TeamDetail/teamDetailHelpers";
import { TeamSidebarSkeleton } from "./TeamLoadingSkeletons";
import { peopleEllipsisSx, peopleSidebarClamp2Sx } from "../peopleFormValidation";

const sidebarCloseButtonSx = {
  p: 0,
  flexShrink: 0,
  overflow: "visible",
  // 24px canvas vs 16px chevrons — nudge so right edges line up without clipping
  mr: "-4px",
  "& svg": {
    width: 24,
    height: 24,
    display: "block",
    overflow: "visible",
  },
};

function DetailChip({ label }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "100%",
        minWidth: 0,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
        color: "#405261",
        fontFamily: "Inter",
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "18px",
        height: "32px",
      }}
    >
      <Typography component="span" sx={{ ...peopleEllipsisSx, fontSize: "inherit" }} title={label}>
        {label}
      </Typography>
    </Box>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ borderBottom: "1px solid #EEF2F6", py: 1.5 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "18px",
          }}
        >
          {title}
        </Typography>
        <Dropdown
          style={{
            width: 16,
            height: 16,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      {open && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Box>
  );
}

function StatItem({ label, value }) {
  return (
    <Box sx={{ flex: 1,  }}>
      <Typography
        sx={{
          color: "#405261",
          fontFamily: "Inter",
          fontSize: 12,
          fontWeight: 400,
          lineHeight: "18px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#061522",
          fontFamily: "Inter",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "18px",
          mt: 0.25,
        }}
      >
        {value.toLocaleString()}
      </Typography>
    </Box>
  );
}

function MemberRow({ member }) {
  const letter = (member.name || "").substring(0, 1).toUpperCase();
  const isAdmin = member.role === "Admin";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
      {member.avatar ? (
        <Avatar src={member.avatar} alt={member.name} sx={{ width: 40, height: 40, flexShrink: 0 }} />
      ) : (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: 12,
            flexShrink: 0,
            bgcolor: colorCodesForAvatar[chracterPosition(letter)] || "#405261",
          }}
        >
          {letter}
        </Avatar>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: "18px",
            ...peopleEllipsisSx,
          }}
          title={member.name}
        >
          {member.name}
        </Typography>
        <Typography
          sx={{
            color: "#405261",
            fontFamily: "Inter",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: "18px",
            ...peopleEllipsisSx,
          }}
          title={member.role}
        >
          {member.role}
        </Typography>
      </Box>
      
    </Box>
  );
}

export default function TeamDetailSidebar({
  team,
  loading = false,
  onClose,
  onViewDetail,
  onEdit,
  canEdit = true,
}) {
  const view = team && !loading ? buildTeamDetailView(team) : null;

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(6, 21, 34, 0.2)",
          zIndex: 1200,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 280,
          height: "100vh",
          bgcolor: "#FFF",
          boxShadow: "0 4px 80px 0 rgba(6, 21, 34, 0.10)",
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
          fontFamily: "Inter",
        }}
      >
        {loading ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 2, flexShrink: 0 }}>
              <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                <CloseIcon />
              </IconButton>
            </Box>
            <TeamSidebarSkeleton />
          </>
        ) : !view ? (
          <Box sx={{ flex: 1, px: 2, py: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#405261" }}>
              Unable to load team details.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      color: "#061522",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "18px",
                      ...peopleEllipsisSx,
                    }}
                    title={view.name}
                  >
                    {view.name}
                  </Typography>
                  {view.description ? (
                    <Typography
                      sx={{
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        mt: 0.5,
                        ...peopleSidebarClamp2Sx,
                      }}
                      title={view.description}
                    >
                      {view.description}
                    </Typography>
                  ) : null}
                </Box>
                <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  borderBottom: "1px solid #E5E9EC",
                  mx: -2,
                  flexShrink: 0,
                  mb: 0.5,
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  borderBottom: "1px solid #EEF2F6",
                  mb: 0.5,
                }}
              >
                <StatItem label="Agents" value={view?.members?.length} />
                <StatItem label="Chats" value={view?.sidebarStats?.totalChats} />
                <StatItem label="Tickets" value={view?.sidebarStats?.totalTickets} />
              </Box>

              <CollapsibleSection title="Groups">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {view.groups.map((g) => (
                    <DetailChip key={g} label={g} />
                  ))}
                </Box>
              </CollapsibleSection>

              <CollapsibleSection title="Channels">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {view.channels.map((c) => (
                    <DetailChip key={c} label={c} />
                  ))}
                </Box>
              </CollapsibleSection>

              <CollapsibleSection title="Team Members">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {view.members.map((m) => (
                    <MemberRow key={m.id} member={m} />
                  ))}
                </Box>
              </CollapsibleSection>
            </Box>

            <Box
              sx={{
                px: 2,
                py: 2,
                flexShrink: 0,
                display: "flex",
                gap: "8px",
              }}
            >
              <Button
                onClick={() => onViewDetail?.()}
                sx={{
                  width: canEdit ? 160 : "100%",
                  minWidth: canEdit ? 160 : "100%",
                  height: 40,
                  borderRadius: "8px",
                  background: "#148DFD",
                  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                  color: "#FFF",
                  fontFamily: "Inter",
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: "18px",
                  textTransform: "none",
                  flexShrink: 0,
                  "&:hover": { background: "#0077CC" },
                }}
              >
                View Detail
              </Button>
              {canEdit ? (
              <Button
                onClick={() => onEdit?.()}
                sx={{
                  width: 80,
                  minWidth: 80,
                  height: 40,
                  borderRadius: "8px",
                  border: "1px solid #D5DCE1",
                  background: "#FFF",
                  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                  color: "#061522",
                  fontFamily: "Inter",
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: "18px",
                  textTransform: "none",
                  flexShrink: 0,
                  "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                }}
              >
                Edit
              </Button>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </>
  );
}
